import { useState, useCallback, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeftRight, Search } from 'lucide-react';

import { MasterPageHeader }   from '@/components/master/MasterPageHeader';
import { MasterLookupBar }   from '@/components/master/MasterLookupBar';
import { MasterFlash }         from '@/components/master/MasterFlash';
import { MasterAuditTab }      from '@/components/master/MasterAuditTab';
import { MasterConfirmDialogs } from '@/components/master/MasterConfirmDialogs';
import { MasterToolbar }       from '@/components/master/MasterToolbar';
import { MasterSearchModal }   from '@/components/master/MasterSearchModal';
import type { SearchColumn }   from '@/components/master/MasterSearchModal';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { ReportHeader }        from '@/components/master/report/ReportHeader';
import { ReportSection }       from '@/components/master/report/ReportSection';
import { ReportFooter }        from '@/components/master/report/ReportFooter';
import {
  Badge, Button, Card, Combobox, Field, Input, Tabs, Toggle,
} from '@/components/ui';

import { useAuth }             from '@/hooks/useAuth';
import { usePermission }       from '@/hooks/usePermission';
import { useFlashMessage }     from '@/hooks/useFlashMessage';
import { useMasterScreenState } from '@/hooks/useMasterScreenState';
import { useMasterRecord, masterListFetcher } from '@/hooks/useMasterRecord';
import { Permissions }         from '@/constants/permissions';
import { exchangeRateApi }     from '@/api/exchangeRate.api';
import type { ExchangeRateDto, ExchangeRateListDto } from '@/types/exchangeRate.types';
import { formatDateTime } from '@/utils/format';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  fromCurrencyCode: z.string().length(3, 'Must be 3 characters'),
  toCurrencyCode:   z.string().length(3, 'Must be 3 characters'),
  effectiveDate:    z.string().min(1, 'Effective date is required'),
  expiryDate:       z.string().nullable().optional(),
  rateType:         z.enum(['STANDARD', 'BUY', 'SELL', 'SPOT']),
  rate:             z.coerce.number().positive('Rate must be > 0'),
  buyRate:          z.coerce.number().min(0),
  sellRate:         z.coerce.number().min(0),
  isManualOverride: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY_FORM: FormValues = {
  fromCurrencyCode: '', toCurrencyCode: '', effectiveDate: '', expiryDate: null,
  rateType: 'STANDARD', rate: 0, buyRate: 0, sellRate: 0, isManualOverride: false,
};

/**
 * Today, by the calendar on the wall rather than in Greenwich.
 *
 * `toISOString()` is UTC, so before 05:30 in Colombo it names yesterday — and a rate filed against
 * the wrong day is not obviously wrong until someone reconciles against it.
 */
const todayIso = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const RATE_TYPES = ['STANDARD', 'BUY', 'SELL', 'SPOT']
  .map(v => ({ value: v, label: v }));

const TABS = ['Rate Details', 'Audit'] as const;
type Tab = (typeof TABS)[number];

const SEARCH_COLUMNS: SearchColumn<ExchangeRateListDto>[] = [
  { key: 'fromCurrencyCode', label: 'From', className: 'w-16 font-mono text-center' },
  { key: 'toCurrencyCode',   label: 'To',   className: 'w-16 font-mono text-center' },
  { key: 'effectiveDate',    label: 'Effective', className: 'w-28' },
  { key: 'expiryDate',       label: 'Expiry', className: 'w-28', render: r => r.expiryDate ?? '—' },
  { key: 'rateType',         label: 'Type',  className: 'w-20' },
  { key: 'rate',             label: 'Rate',  className: 'w-24', render: r => r.rate.toFixed(6) },
  { key: 'isApproved', label: 'Approved', className: 'w-24',
    render: r => <Badge variant={r.isApproved ? 'success' : 'default'}>{r.isApproved ? 'Approved' : 'Pending'}</Badge> },
];

function dtoToForm(dto: ExchangeRateDto): FormValues {
  return {
    fromCurrencyCode: dto.fromCurrencyCode, toCurrencyCode: dto.toCurrencyCode,
    effectiveDate: dto.effectiveDate, expiryDate: dto.expiryDate ?? null,
    rateType: dto.rateType as 'STANDARD' | 'BUY' | 'SELL' | 'SPOT',
    rate: dto.rate, buyRate: dto.buyRate, sellRate: dto.sellRate,
    isManualOverride: dto.isManualOverride,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export const ExchangeRatePage = () => {
  const { user }          = useAuth();
  const { hasPermission } = usePermission();

  const canView   = hasPermission(Permissions.MST_EXCHANGE_RATE_VIEW);
  const canCreate = hasPermission(Permissions.MST_EXCHANGE_RATE_CREATE);
  const canEdit   = hasPermission(Permissions.MST_EXCHANGE_RATE_EDIT);
  const canDelete = hasPermission(Permissions.MST_EXCHANGE_RATE_DELETE);

  const screen = useMasterScreenState({ canEdit, canDelete });

  const { flashMsg, flash } = useFlashMessage();


  // Composite lookup bar state
  const [fromInput,    setFromInput]    = useState('');
  const [toInput,      setToInput]      = useState('');
  const [dateInput,    setDateInput]    = useState(todayIso());

  const [showNewConfirm,    setShowNewConfirm]    =
    useState<{ from: string; to: string; date: string } | null>(null);

  const {
    register, handleSubmit, control, reset, watch,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY_FORM });

  const isManualOverride = watch('isManualOverride');

  const master = useMasterRecord<ExchangeRateDto, Tab, FormValues>({
    screen, flash, firstTab: 'Rate Details',
    clearForm:   () => reset(EMPTY_FORM),
    clearScreen: () => { setFromInput(''); setToInput(''); setDateInput(todayIso()); },
    onDelete: {
      remove:   id => exchangeRateApi.remove(id),
      describe: r => `Rate ${r.fromCurrencyCode} → ${r.toCurrencyCode} on ${r.effectiveDate}`,
    },
  });

  const {
    record, setRecord, activeTab, setActiveTab, saving, setSaving, lookupBusy, setLookupBusy,
    printOpen, setPrintOpen, searchOpen, setSearchOpen,
    pendingSave, setPendingSave, showSaveConfirm, setShowSaveConfirm,
    showDeleteConfirm, setShowDeleteConfirm, resetToInitial, executeDelete, beginSave,
  } = master;


  const loadRecord = useCallback((dto: ExchangeRateDto) => {
    setRecord(dto); reset(dtoToForm(dto));
    setFromInput(dto.fromCurrencyCode); setToInput(dto.toCurrencyCode);
    setDateInput(dto.effectiveDate);
    screen.setMode('view'); setActiveTab('Rate Details');
  }, [reset, screen]);

  const handleSearch = async () => {
    if (!canView) { flash('error', 'You do not have permission to view exchange rates.'); return; }

    const from = fromInput.trim().toUpperCase();
    const to   = toInput.trim().toUpperCase();

    // All three, every time. A rate is identified by the pair AND the date, and a blank field used
    // to drop out of the query rather than narrow it: searching with everything empty opened
    // whichever rate happened to be first in the company, and filling only From opened a rate for
    // a currency and a date nobody had asked about. Neither looked like an error.
    const missing = [
      !from && 'a from currency',
      !to && 'a to currency',
      !dateInput && 'a date',
    ].filter(Boolean);

    if (missing.length > 0) {
      flash('error', `A rate is identified by both currencies and a date. Enter ${missing.join(', ')}.`);
      return;
    }

    if (from === to) {
      flash('error', 'A currency does not need a rate against itself. Choose two different currencies.');
      return;
    }

    setLookupBusy(true);
    try {
      const res = await exchangeRateApi.getList({
        fromCurrencyCode: from,
        toCurrencyCode:   to,
        effectiveDate:    dateInput,
        pageSize: 1,
      });
      const items = res.data.data?.items ?? [];
      if (items.length > 0) {
        const full = await exchangeRateApi.getById(items[0].id);
        if (full.data.data) loadRecord(full.data.data);
      } else {
        if (!canCreate) { flash('error', `No rate for ${from} → ${to} on ${dateInput}.`); return; }
        // Ask, as the other seven masters do. This screen used to drop straight into a blank new
        // record, so a mistyped currency looked identical to a rate that genuinely did not exist.
        setShowNewConfirm({ from, to, date: dateInput });
      }
    } catch {
      flash('error', 'Search failed. Please try again.');
    } finally { setLookupBusy(false); }
  };

  /** Enter searches, wherever the caret is in the three key fields. */
  const onLookupKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); void handleSearch(); }
  };

  const handleConfirmCreateNew = () => {
    const pending = showNewConfirm; setShowNewConfirm(null);
    if (!pending) return;
    reset({
      ...EMPTY_FORM,
      fromCurrencyCode: pending.from,
      toCurrencyCode:   pending.to,
      effectiveDate:    pending.date,
    });
    setRecord(null); screen.setMode('new'); setActiveTab('Rate Details');
  };

  const handleSaveClick = () => {
    // The second argument runs when the form is invalid. Without it a failed validation did
    // nothing at all -- no save, no message, no focus -- so the button looked broken rather than
    // the form looking wrong.
    handleSubmit(beginSave, (formErrors) => {
      const first = Object.values(formErrors)[0]?.message;
      flash('error', first ? String(first) : 'Please correct the highlighted fields.');
    })();
  };

  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false); setSaving(true);
    try {
      const d = pendingSave;
      if (screen.isNew) {
        await exchangeRateApi.create({
          fromCurrencyCode: d.fromCurrencyCode.toUpperCase(),
          toCurrencyCode:   d.toCurrencyCode.toUpperCase(),
          rate:             d.rate,
          buyRate:          d.buyRate,
          sellRate:         d.sellRate,
          effectiveDate:    d.effectiveDate,
          expiryDate:       d.expiryDate || null,
          rateType:         d.rateType,
          isManualOverride: d.isManualOverride,
        });
        flash('success', 'Exchange rate created successfully.');
        resetToInitial();
      } else if (screen.isEdit && record) {
        await exchangeRateApi.update(record.id, {
          rate:             d.rate,
          buyRate:          d.buyRate,
          sellRate:         d.sellRate,
          effectiveDate:    d.effectiveDate,
          expiryDate:       d.expiryDate || null,
          rateType:         d.rateType,
          isManualOverride: d.isManualOverride,
        });
        flash('success', 'Exchange rate updated successfully.');
        resetToInitial();
      }
    } catch (err: unknown) {
      const body = (err as { response?: { data?: { message?: string;
                       errors?: { message?: string }[] } } }).response?.data;
      // errors[0] names the field. The envelope's own message is just "Validation failed."
      const msg = body?.errors?.[0]?.message ?? body?.message ?? 'Save failed.';
      flash('error', msg);
    } finally { setSaving(false); setPendingSave(null); }
  };


  const fetchRates = useMemo(() => masterListFetcher(exchangeRateApi.getList), []);

  const handleSearchSelect = (item: ExchangeRateListDto) => {
    setSearchOpen(false); setLookupBusy(true);
    exchangeRateApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load exchange rate.'))
      .finally(() => setLookupBusy(false));
  };

  const fieldsDisabled = screen.isInitial || screen.isView;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">
      <MasterPageHeader icon={ArrowLeftRight} title="Exchange Rate Maintenance"
        subtitle="Manage currency conversion rates" screen={screen} />

      <MasterToolbar
        {...screen.toolbar} {...master.toolbar}
        saving={saving} loading={lookupBusy} onSave={handleSaveClick}
      />

      <MasterFlash message={flashMsg} />

      <MasterLookupBar
        label="Search Rate"
        hint={screen.isInitial
          ? 'A rate is one pair on one date — fill all three, then Search or press Enter.'
          : undefined}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-gray-400">From</span>
            <Input
              size="sm"
              type="text"
              value={fromInput}
              onChange={e => setFromInput(e.target.value.toUpperCase())}
              onKeyDown={onLookupKey}
              disabled={screen.isNew || screen.isEdit || saving}
              placeholder="USD"
              maxLength={3}
              className="w-16 px-2 font-mono text-center uppercase"
            />
          </div>
          <ArrowLeftRight size={12} className="text-gray-400 flex-shrink-0" />
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-gray-400">To</span>
            <Input
              size="sm"
              type="text"
              value={toInput}
              onChange={e => setToInput(e.target.value.toUpperCase())}
              onKeyDown={onLookupKey}
              disabled={screen.isNew || screen.isEdit || saving}
              placeholder="LKR"
              maxLength={3}
              className="w-16 px-2 font-mono text-center uppercase"
            />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-gray-400">Date</span>
            <Input
              size="sm"
              type="date"
              value={dateInput}
              onChange={e => setDateInput(e.target.value)}
              onKeyDown={onLookupKey}
              disabled={screen.isNew || screen.isEdit || saving}
              className="w-auto px-2"
            />
          </div>
          <Button
            size="sm"
            className="text-xs"
            leftIcon={<Search size={12} />}
            loading={lookupBusy}
            onClick={handleSearch}
            disabled={screen.isNew || screen.isEdit || saving}
          >
            Search
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => setSearchOpen(true)}
            disabled={screen.isNew || screen.isEdit || saving}
          >
            Browse All
          </Button>
        </div>
        {record && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="font-mono text-xs font-bold text-gray-700 dark:text-gray-300">
              {record.fromCurrencyCode} → {record.toCurrencyCode}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{record.effectiveDate}</span>
            <Badge variant={record.isApproved ? 'success' : 'default'}>{record.isApproved ? 'Approved' : 'Pending'}</Badge>
          </div>
        )}
      </MasterLookupBar>

      {/* Main content */}
      <div className="flex-1 overflow-auto">
        <form onSubmit={e => e.preventDefault()} noValidate>
          <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

          <div className="p-5 space-y-5 max-w-2xl">

            {activeTab === 'Rate Details' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Rate Details</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">

                  <Input label="From Currency" {...register('fromCurrencyCode')} disabled
                    className="font-mono uppercase text-center" error={errors.fromCurrencyCode?.message} />

                  <Input label="To Currency" {...register('toCurrencyCode')} disabled
                    className="font-mono uppercase text-center" error={errors.toCurrencyCode?.message} />

                  <Controller name="rateType" control={control} render={({ field }) => (
                    <Combobox label="Rate Type" options={RATE_TYPES} clearable={false}
                      value={field.value} onChange={field.onChange}
                      disabled={fieldsDisabled} error={errors.rateType?.message} />
                  )} />

                  <Input label="Effective Date *" type="date" {...register('effectiveDate')}
                    disabled={fieldsDisabled} error={errors.effectiveDate?.message} />

                  <Input label="Expiry Date" type="date" {...register('expiryDate')} disabled={fieldsDisabled} />

                  <Input label="Exchange Rate *" type="number" {...register('rate')} disabled={fieldsDisabled}
                    step="0.000001" min={0} placeholder="0.000000" className="font-mono"
                    error={errors.rate?.message} />

                  <Input label="Buy Rate" type="number" {...register('buyRate')} disabled={fieldsDisabled}
                    step="0.000001" min={0} placeholder="0.000000" className="font-mono" />

                  <Input label="Sell Rate" type="number" {...register('sellRate')} disabled={fieldsDisabled}
                    step="0.000001" min={0} placeholder="0.000000" className="font-mono" />

                  {/* Approval status — VIEW only badge */}
                  {(screen.isView || screen.isEdit) && record && (
                    <Field label="Approval"
                      hint={record.approvedBy ? `by ${record.approvedBy} on ${record.approvedDate}` : undefined}>
                      <Badge variant={record.isApproved ? 'success' : 'default'}>
                        {record.isApproved ? 'Approved' : 'Pending Approval'}
                      </Badge>
                    </Field>
                  )}

                  <div className="col-span-2 sm:col-span-3">
                    <Controller name="isManualOverride" control={control} render={({ field }) => (
                      <Toggle
                        label="Manual Override"
                        description="Rate was set manually rather than imported from a feed."
                        tone="warning"
                        checked={field.value}
                        onChange={field.onChange}
                        disabled={fieldsDisabled}
                      />
                    )} />
                    {isManualOverride && (
                      <div className="mt-2 p-2 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-[11px] text-amber-700 dark:text-amber-400">
                        This rate was entered manually and requires approval before use.
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {activeTab === 'Audit' && <MasterAuditTab record={record} />}
          </div>
        </form>
      </div>

      {/* Modals */}
      <MasterSearchModal<ExchangeRateListDto>
        isOpen={searchOpen} onClose={() => setSearchOpen(false)}
        title="Browse Exchange Rates" columns={SEARCH_COLUMNS}
        fetchData={fetchRates} onSelect={handleSearchSelect}
      />

      <MasterConfirmDialogs
        entity="Exchange rate"
        screen={screen}
        record={record}
        describe={r => `${r.fromCurrencyCode} → ${r.toCurrencyCode} on ${r.effectiveDate}`}
        saving={saving}
        create={{
          label: showNewConfirm && `${showNewConfirm.from} → ${showNewConfirm.to} on ${showNewConfirm.date}`,
          onConfirm: handleConfirmCreateNew,
          onCancel: () => setShowNewConfirm(null),
        }}
        saveOpen={showSaveConfirm}
        onSaveConfirm={executeSave}
        onSaveCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
        deleteOpen={showDeleteConfirm}
        onDeleteConfirm={executeDelete}
        onDeleteCancel={() => setShowDeleteConfirm(false)}
      />

      {record && (
        <MasterReportPreview isOpen={printOpen} onClose={() => setPrintOpen(false)} title="Exchange Rate Report">
          <ReportHeader reportTitle="Exchange Rate Report"
            printedBy={user?.fullName ?? user?.username} printedAt={new Date()}
            meta={[
              { label: 'Currency Pair', value: `${record.fromCurrencyCode} → ${record.toCurrencyCode}` },
              { label: 'Effective',     value: record.effectiveDate },
            ]}
          />
          <ReportSection title="Rate Details" fields={[
            { label: 'From Currency',  value: record.fromCurrencyCode },
            { label: 'To Currency',    value: record.toCurrencyCode },
            { label: 'Rate Type',      value: record.rateType },
            { label: 'Exchange Rate',  value: record.rate.toFixed(6) },
            { label: 'Buy Rate',       value: record.buyRate.toFixed(6) },
            { label: 'Sell Rate',      value: record.sellRate.toFixed(6) },
            { label: 'Effective Date', value: record.effectiveDate },
            { label: 'Expiry Date',    value: record.expiryDate ?? '—' },
            { label: 'Manual Override', value: record.isManualOverride, display: record.isManualOverride ? 'Yes' : 'No' },
            { label: 'Approved',       value: record.isApproved, display: record.isApproved ? 'Yes' : 'No' },
            { label: 'Approved By',    value: record.approvedBy },
            { label: 'Approved Date',  value: record.approvedDate },
          ]} />
          <ReportSection title="Audit Information" fields={[
            { label: 'Created By',    value: record.createdByName ?? record.createdBy },
            { label: 'Created Date',  value: formatDateTime(record.createdDate) },
            { label: 'Modified By',   value: record.modifiedByName ?? record.modifiedBy },
            { label: 'Modified Date', value: formatDateTime(record.modifiedDate) },
          ]} />
          <ReportFooter />
        </MasterReportPreview>
      )}
    </div>
  );
};
